import { useState, useCallback, useMemo } from 'react'

export const useSatsueiMemoCategory = () => {
  // Main state
  const [categories, setCategories] = useState([])
  const [selectedCategoryId, setSelectedCategoryId] = useState(0)
  const [selectedColumnId, setSelectedColumnId] = useState(null)
  const [selectedColumnValueId, setSelectedColumnValueId] = useState(null)
  
  // Validation state
  const [valueCheckFlag, setValueCheckFlag] = useState(false)
  const [categoryNameList, setCategoryNameList] = useState([])
  const [allColumnList, setAllColumnList] = useState({})
  
  // Temporary editing state
  const [tempCategoryNames, setTempCategoryNames] = useState({})
  const [tempColumnNames, setTempColumnNames] = useState({})

  // Helper functions
  const getMaxCategoryId = useCallback(() => {
    return Math.max(...categories.map(c => c.satsueiMemoCategoryId), 0)
  }, [categories])

  const getMaxColumnId = useCallback((categoryId) => {
    const category = categories.find(c => c.satsueiMemoCategoryId === categoryId)
    if (!category) return 0
    return Math.max(...category.satsueiMemoColumnList?.map(col => col.satsueiMemoColumnId) || [0], 0)
  }, [categories])

  const getMaxColumnValueId = useCallback((categoryId, columnId) => {
    const category = categories.find(c => c.satsueiMemoCategoryId === categoryId)
    if (!category) return 0
    const column = category.satsueiMemoColumnList?.find(col => col.satsueiMemoColumnId === columnId)
    if (!column) return 0
    return Math.max(...column.satsueiMemoColumnValueList?.map(val => val.satsueiMemoColumnValueId) || [0], 0)
  }, [categories])

  const isContainCategoryName = useCallback((categoryName, excludeCategoryId = null) => {
    return categories.some(c => 
      c.satsueiMemoCategoryName === categoryName && 
      c.satsueiMemoCategoryId !== excludeCategoryId
    )
  }, [categories])

  const isContainColumnName = useCallback((categoryId, columnName, columnType, excludeColumnId = null) => {
    const category = categories.find(c => c.satsueiMemoCategoryId === categoryId)
    if (!category) {
      return false
    }
    const result = category.satsueiMemoColumnList?.some(col => {
      const matches = col.satsueiMemoColumnName === columnName && 
        parseInt(col.columnType) === parseInt(columnType) &&
        col.satsueiMemoColumnId !== excludeColumnId &&
        col.deleteFlg === 0
      return matches
    }) || false
    return result
  }, [categories])

  const isContainColumnValueName = useCallback((categoryId, columnId, valueName, excludeValueId = null) => {
    const category = categories.find(c => c.satsueiMemoCategoryId === categoryId)
    if (!category) return false
    const column = category.satsueiMemoColumnList?.find(col => col.satsueiMemoColumnId === columnId)
    if (!column) return false
    return column.satsueiMemoColumnValueList?.some(val => 
      val.satsueiMemoColumnValueName === valueName &&
      val.satsueiMemoColumnValueId !== excludeValueId
    ) || false
  }, [categories])

  // Data operations - these now only update local state, not immediately applied
  const addCategory = useCallback((categoryName) => {
    const maxId = getMaxCategoryId()
    if (maxId >= 999) {
      throw new Error('撮影メモカテゴリーはこれ以上追加出来ません。')
    }

    if (isContainCategoryName(categoryName)) {
      throw new Error(`「${categoryName}」はすでに登録されています。`)
    }

    // Calculate the next order index based on existing categories (excluding 未指定 which has orderIndex 0)
    const existingCategories = categories.filter(c => c.deleteFlg === 0 && c.satsueiMemoCategoryId !== 0)
    const maxOrderIndex = Math.max(...existingCategories.map(c => c.orderIndex || 0), 0)

    const newCategory = {
      satsueiMemoCategoryId: maxId + 1,
      satsueiMemoCategoryName: categoryName,
      satsueiMemoColumnList: [],
      deleteFlg: 0,
      orderIndex: maxOrderIndex + 1, // Set the order index to be at the end
      isNew: true // Mark as new
    }

    setCategories(prev => [...prev, newCategory])
    setCategoryNameList(prev => [...prev, `${newCategory.satsueiMemoCategoryId}${categoryName}`])
    setAllColumnList(prev => ({ ...prev, [`ct${newCategory.satsueiMemoCategoryId}`]: [] }))

    return newCategory
  }, [getMaxCategoryId, isContainCategoryName, categories])

  // Temporary update function - no validation, just updates temp state
  const updateCategoryTemp = useCallback((categoryId, categoryName) => {
    setTempCategoryNames(prev => ({
      ...prev,
      [categoryId]: categoryName
    }))
  }, [])

  // Final update function - with validation
  const updateCategory = useCallback((categoryId, categoryName) => {
    if (!categoryName.trim()) {
      throw new Error('撮影メモカテゴリー名が空になっています。')
    }

    if (isContainCategoryName(categoryName, categoryId)) {
      throw new Error(`撮影メモカテゴリー名「${categoryName}」が重複しています。`)
    }

    setCategories(prev => prev.map(c => 
      c.satsueiMemoCategoryId === categoryId 
        ? { ...c, satsueiMemoCategoryName: categoryName }
        : c
    ))

    // Clear temp state after successful update
    setTempCategoryNames(prev => {
      const newTemp = { ...prev }
      delete newTemp[categoryId]
      return newTemp
    })
  }, [isContainCategoryName])

  // Cancel editing - clear temp state
  const cancelCategoryEdit = useCallback((categoryId) => {
    setTempCategoryNames(prev => {
      const newTemp = { ...prev }
      delete newTemp[categoryId]
      return newTemp
    })
  }, [])

  const deleteCategory = useCallback((categoryId) => {
    setCategories(prev => prev.map(c => 
      c.satsueiMemoCategoryId === categoryId 
        ? { ...c, deleteFlg: 1 }
        : c
    ))
  }, [])

  const restoreCategory = useCallback((categoryId) => {
    setCategories(prev => prev.map(c => 
      c.satsueiMemoCategoryId === categoryId 
        ? { ...c, deleteFlg: 0 }
        : c
    ))
  }, [])

  const addColumn = useCallback((categoryId, columnName, columnType, requiredFlg = 0) => {
    // Convert columnType to number to ensure consistent comparison
    const normalizedColumnType = parseInt(columnType)

    if (!columnName.trim()) {
      throw new Error('撮影メモ項目名を入力してください')
    }

    if (isContainColumnName(categoryId, columnName, normalizedColumnType)) {
      throw new Error(`「${columnName}」はすでに登録されています。`)
    }

    const maxId = getMaxColumnId(categoryId)

    // Calculate the next order index based on existing active columns in this category
    const category = categories.find(c => c.satsueiMemoCategoryId === categoryId)
    const existingColumns = category?.satsueiMemoColumnList?.filter(col => col.deleteFlg === 0) || []
    const maxOrderIndex = existingColumns.length > 0 
      ? Math.max(...existingColumns.map(col => col.orderIndex ?? 0))
      : -1

    const newColumn = {
      satsueiMemoCategoryId: categoryId,
      satsueiMemoColumnId: maxId + 1,
      satsueiMemoColumnName: columnName,
      columnType: normalizedColumnType,
      requiredFlg: requiredFlg,
      satsueiMemoColumnValueList: [],
      deleteFlg: 0,
      orderIndex: maxOrderIndex + 1, // Set the order index to be at the end
      isNew: true // Mark as new
    }

    setCategories(prev => prev.map(c => 
      c.satsueiMemoCategoryId === categoryId
        ? { ...c, satsueiMemoColumnList: [...(c.satsueiMemoColumnList || []), newColumn] }
        : c
    ))

    // Update allColumnList
    setAllColumnList(prev => ({
      ...prev,
      [`ct${categoryId}`]: [...(prev[`ct${categoryId}`] || []), `${normalizedColumnType}${columnName}`]
    }))

    return newColumn
  }, [getMaxColumnId, isContainColumnName])

  // Temporary update function for columns - no validation, just updates temp state
  const updateColumnTemp = useCallback((categoryId, columnId, columnName) => {
    const key = `${categoryId}-${columnId}`
    setTempColumnNames(prev => ({
      ...prev,
      [key]: columnName
    }))
  }, [])

  // Final update function for columns - with validation
  const updateColumn = useCallback((categoryId, columnId, columnName, requiredFlg) => {
    if (!columnName.trim()) {
      throw new Error('撮影メモ項目が空になっています。')
    }

    // Get the existing column to get its column type
    const category = categories.find(c => c.satsueiMemoCategoryId === categoryId)
    const existingColumn = category?.satsueiMemoColumnList?.find(col => col.satsueiMemoColumnId === columnId)
    const columnType = existingColumn?.columnType

    if (isContainColumnName(categoryId, columnName, columnType, columnId)) {
      throw new Error(`「${columnName}」はすでに登録されています。`)
    }

    setCategories(prev => prev.map(c => 
      c.satsueiMemoCategoryId === categoryId
        ? {
            ...c,
            satsueiMemoColumnList: c.satsueiMemoColumnList?.map(col =>
              col.satsueiMemoColumnId === columnId
                ? { ...col, satsueiMemoColumnName: columnName, requiredFlg }
                : col
            ) || []
          }
        : c
    ))

    // Clear temp state after successful update
    const key = `${categoryId}-${columnId}`
    setTempColumnNames(prev => {
      const newTemp = { ...prev }
      delete newTemp[key]
      return newTemp
    })
  }, [isContainColumnName, categories])

  // Cancel column editing - clear temp state
  const cancelColumnEdit = useCallback((categoryId, columnId) => {
    const key = `${categoryId}-${columnId}`
    setTempColumnNames(prev => {
      const newTemp = { ...prev }
      delete newTemp[key]
      return newTemp
    })
  }, [])

  const deleteColumn = useCallback((categoryId, columnId) => {
    setCategories(prev => prev.map(c => 
      c.satsueiMemoCategoryId === categoryId
        ? {
            ...c,
            satsueiMemoColumnList: c.satsueiMemoColumnList?.map(col =>
              col.satsueiMemoColumnId === columnId
                ? { ...col, deleteFlg: 1 }
                : col
            ) || []
          }
        : c
    ))
  }, [])

  const restoreColumn = useCallback((categoryId, columnId) => {
    setCategories(prev => prev.map(c => 
      c.satsueiMemoCategoryId === categoryId
        ? {
            ...c,
            satsueiMemoColumnList: c.satsueiMemoColumnList?.map(col =>
              col.satsueiMemoColumnId === columnId
                ? { ...col, deleteFlg: 0 }
                : col
            ) || []
          }
        : c
    ))
  }, [])

  const addColumnValue = useCallback((categoryId, columnId, valueName, initialValueFlg = 0) => {
    if (!valueName.trim()) {
      throw new Error('撮影メモ項目値名を入力してください')
    }

    if (isContainColumnValueName(categoryId, columnId, valueName)) {
      throw new Error(`「${valueName}」はすでに登録されています。`)
    }

    // Check column value count limit
    const column = categories.find(c => c.satsueiMemoCategoryId === categoryId)
      ?.satsueiMemoColumnList?.find(col => col.satsueiMemoColumnId === columnId)
    
    if (column) {
      const activeValueCount = column.satsueiMemoColumnValueList?.filter(v => v.deleteFlg === 0).length || 0
      const columnType = parseInt(column.columnType)
      
      // Check specific limit for 2段階選択 (columnType 11)
      if (columnType === 11) {
        if (activeValueCount >= 2) {
          throw new Error('2段階選択は2つまでしか登録できません。')
        }
      } else if (columnType === 12) {
        // Check specific limit for 3段階選択 (columnType 12)
        if (activeValueCount >= 3) {
          throw new Error('3段階選択は3つまでしか登録できません。')
        }
      } else if (columnType === 13) {
        // Check specific limit for 5段階選択 (columnType 13)
        if (activeValueCount >= 5) {
          throw new Error('5段階選択は5つまでしか登録できません。')
        }
      } else if (columnType === 14) {
        // Check specific limit for 10段階選択 (columnType 14)
        if (activeValueCount >= 10) {
          throw new Error('10段階選択は10つまでしか登録できません。')
        }
      } else if (activeValueCount >= 63) {
        // General limit for other column types
        throw new Error('項目値は63個までしか登録できません。')
      }
    }

    const maxId = getMaxColumnValueId(categoryId, columnId)
    const newValue = {
      satsueiMemoCategoryId: categoryId,
      satsueiMemoColumnId: columnId,
      satsueiMemoColumnValueId: maxId + 1,
      satsueiMemoColumnValueName: valueName,
      initialValueFlg: initialValueFlg,
      deleteFlg: 0,
      isNew: true // Mark as new
    }

    setCategories(prev => prev.map(c => 
      c.satsueiMemoCategoryId === categoryId
        ? {
            ...c,
            satsueiMemoColumnList: c.satsueiMemoColumnList?.map(col =>
              col.satsueiMemoColumnId === columnId
                ? { ...col, satsueiMemoColumnValueList: [...(col.satsueiMemoColumnValueList || []), newValue] }
                : col
            ) || []
          }
        : c
    ))

    return newValue
  }, [getMaxColumnValueId, isContainColumnValueName, categories])

  const deleteColumnValue = useCallback((categoryId, columnId, valueId) => {
    setCategories(prev => prev.map(c => 
      c.satsueiMemoCategoryId === categoryId
        ? {
            ...c,
            satsueiMemoColumnList: c.satsueiMemoColumnList?.map(col =>
              col.satsueiMemoColumnId === columnId
                ? {
                    ...col,
                    satsueiMemoColumnValueList: col.satsueiMemoColumnValueList?.map(val =>
                      val.satsueiMemoColumnValueId === valueId
                        ? { ...val, deleteFlg: 1 }
                        : val
                    ) || []
                  }
                : col
            ) || []
          }
        : c
    ))
  }, [])

  const restoreColumnValue = useCallback((categoryId, columnId, valueId) => {
    // Check if restoring would exceed limit for 2段階選択
    const column = categories.find(c => c.satsueiMemoCategoryId === categoryId)
      ?.satsueiMemoColumnList?.find(col => col.satsueiMemoColumnId === columnId)
    
    if (column) {
      const columnType = parseInt(column.columnType)
      const activeValueCount = column.satsueiMemoColumnValueList?.filter(v => v.deleteFlg === 0).length || 0
      
      // Check specific limit for 2段階選択 (columnType 11)
      if (columnType === 11 && activeValueCount >= 2) {
        throw new Error('2段階選択は2つまでしか登録できません。')
      }
      // Check specific limit for 3段階選択 (columnType 12)
      if (columnType === 12 && activeValueCount >= 3) {
        throw new Error('3段階選択は3つまでしか登録できません。')
      }
      // Check specific limit for 5段階選択 (columnType 13)
      if (columnType === 13 && activeValueCount >= 5) {
        throw new Error('5段階選択は5つまでしか登録できません。')
      }
      // Check specific limit for 10段階選択 (columnType 14)
      if (columnType === 14 && activeValueCount >= 10) {
        throw new Error('10段階選択は10つまでしか登録できません。')
      }
    }

    setCategories(prev => prev.map(c => 
      c.satsueiMemoCategoryId === categoryId
        ? {
            ...c,
            satsueiMemoColumnList: c.satsueiMemoColumnList?.map(col =>
              col.satsueiMemoColumnId === columnId
                ? {
                    ...col,
                    satsueiMemoColumnValueList: col.satsueiMemoColumnValueList?.map(val =>
                      val.satsueiMemoColumnValueId === valueId
                        ? { ...val, deleteFlg: 0 }
                        : val
                    ) || []
                  }
                : col
            ) || []
          }
        : c
    ))
  }, [categories])

  const updateColumnInitialValue = useCallback((categoryId, columnId, initialValue) => {
    setCategories(prev => prev.map(c => 
      c.satsueiMemoCategoryId === categoryId
        ? {
            ...c,
            satsueiMemoColumnList: c.satsueiMemoColumnList?.map(col =>
              col.satsueiMemoColumnId === columnId
                ? { ...col, initialValue }
                : col
            ) || []
          }
        : c
    ))
  }, [])

  const updateColumnValue = useCallback((categoryId, columnId, valueId, valueName, initialValueFlg) => {
    if (!valueName.trim()) {
      throw new Error('撮影メモ項目値名を入力してください')
    }

    if (isContainColumnValueName(categoryId, columnId, valueName, valueId)) {
      throw new Error(`「${valueName}」はすでに登録されています。`)
    }

    setCategories(prev => prev.map(c => 
      c.satsueiMemoCategoryId === categoryId
        ? {
            ...c,
            satsueiMemoColumnList: c.satsueiMemoColumnList?.map(col =>
              col.satsueiMemoColumnId === columnId
                ? {
                    ...col,
                    satsueiMemoColumnValueList: col.satsueiMemoColumnValueList?.map(val =>
                      val.satsueiMemoColumnValueId === valueId
                        ? { ...val, satsueiMemoColumnValueName: valueName, initialValueFlg }
                        : val
                    ) || []
                  }
                : col
            ) || []
          }
        : c
    ))
  }, [isContainColumnValueName])

  // Computed values
  const activeCategories = useMemo(() => 
    categories.filter(c => c.deleteFlg === 0), [categories]
  )

  const deletedCategories = useMemo(() => 
    categories.filter(c => c.deleteFlg === 1), [categories]
  )

  const selectedCategory = useMemo(() => 
    categories.find(c => c.satsueiMemoCategoryId === selectedCategoryId), [categories, selectedCategoryId]
  )

  const activeColumns = useMemo(() => 
    selectedCategory?.satsueiMemoColumnList?.filter(col => col.deleteFlg === 0) || [], [selectedCategory]
  )

  const deletedColumns = useMemo(() => 
    selectedCategory?.satsueiMemoColumnList?.filter(col => col.deleteFlg === 1) || [], [selectedCategory]
  )

  const selectedColumn = useMemo(() => 
    selectedCategory?.satsueiMemoColumnList?.find(col => col.satsueiMemoColumnId === selectedColumnId), 
    [selectedCategory, selectedColumnId]
  )

  const activeColumnValues = useMemo(() => 
    selectedColumn?.satsueiMemoColumnValueList?.filter(val => val.deleteFlg === 0) || [], [selectedColumn]
  )

  const deletedColumnValues = useMemo(() => 
    selectedColumn?.satsueiMemoColumnValueList?.filter(val => val.deleteFlg === 1) || [], [selectedColumn]
  )

  // Validation
  const validateRegistration = useCallback(() => {
    let hasError = false
    const errorMessages = []

    categories.forEach(category => {
      if (category.deleteFlg === 0) {
        category.satsueiMemoColumnList?.forEach(column => {
          if (column.deleteFlg === 0) {
            const columnType = parseInt(column.columnType)
            if ([5, 6, 7, 8, 11, 12, 13, 14].includes(columnType)) {
              const valueCount = column.satsueiMemoColumnValueList?.filter(v => v.deleteFlg === 0).length || 0
              if (valueCount === 0) {
                hasError = true
                errorMessages.push(`${category.satsueiMemoCategoryName}の「${column.satsueiMemoColumnName}」に項目値が登録されていません。`)
              }
            }
          }
        })
      }
    })

    if (hasError) {
      throw new Error(errorMessages.join('\n'))
    }
  }, [categories])

  // Data preparation for API - Categories
  const prepareNewCategoryData = useCallback(() => {
    return categories.filter(category => 
      category.deleteFlg === 0 && 
      category.isNew === true // Only truly new categories
    ).map((category) => ({
      satsueiMemoCategoryId: category.satsueiMemoCategoryId,
      satsueiMemoCategoryName: category.satsueiMemoCategoryName,
      orderIndex: category.orderIndex
    }))
  }, [categories])

  const prepareUpdatedCategoryData = useCallback(() => {
    return categories.filter(category => 
      category.deleteFlg === 0 && 
      category.isNew !== true // Existing categories that are not new
    ).map((category) => ({
      satsueiMemoCategoryId: category.satsueiMemoCategoryId,
      satsueiMemoCategoryName: category.satsueiMemoCategoryName,
      orderIndex: category.orderIndex,
      deleteFlg: category.deleteFlg
    }))
  }, [categories])

  const prepareRemovedCategoryData = useCallback(() => {
    return categories.filter(category => category.deleteFlg === 1).map(category => ({
      satsueiMemoCategoryId: category.satsueiMemoCategoryId
    }))
  }, [categories])

  // Data preparation for API - Columns
  const prepareNewColumnData = useCallback(() => {
    const columns = []
    categories.forEach(category => {
      // Filter new columns first to get correct order
      const newColumns = category.satsueiMemoColumnList?.filter(column => 
        column.deleteFlg === 0 && column.isNew === true
      ) || []
      
      newColumns.forEach((column, newIndex) => {
        columns.push({
          satsueiMemoCategoryId: category.satsueiMemoCategoryId,
          satsueiMemoColumnId: column.satsueiMemoColumnId,
          satsueiMemoColumnName: column.satsueiMemoColumnName,
          columnType: column.columnType,
          requiredFlg: column.requiredFlg || 0,
          initialValue: column.initialValue,
          orderIndex: column.orderIndex || newIndex
        })
      })
    })
    return columns
  }, [categories])

  const prepareUpdatedColumnData = useCallback(() => {
    const columns = []
    categories.forEach(category => {
      // Filter active, non-new columns first to get correct order
      const activeColumns = category.satsueiMemoColumnList?.filter(column => 
        column.deleteFlg === 0 && column.isNew !== true
      ) || []
      
      activeColumns.forEach((column, activeIndex) => {
        columns.push({
          satsueiMemoCategoryId: category.satsueiMemoCategoryId,
          satsueiMemoColumnId: column.satsueiMemoColumnId,
          satsueiMemoColumnName: column.satsueiMemoColumnName,
          columnType: column.columnType,
          requiredFlg: column.requiredFlg || 0,
          initialValue: column.initialValue,
          orderIndex: column.orderIndex || activeIndex,
          deleteFlg: column.deleteFlg
        })
      })
    })
    return columns
  }, [categories])

  const prepareRemovedColumnData = useCallback(() => {
    const columns = []
    categories.forEach(category => {
      category.satsueiMemoColumnList?.forEach(column => {
        if (column.deleteFlg === 1) {
          columns.push({
            satsueiMemoCategoryId: category.satsueiMemoCategoryId,
            satsueiMemoColumnId: column.satsueiMemoColumnId
          })
        }
      })
    })
    return columns
  }, [categories])

  // Data preparation for API - Column Values
  const prepareNewColumnValueData = useCallback(() => {
    const values = []
    categories.forEach(category => {
      category.satsueiMemoColumnList?.forEach(column => {
        // Filter new values first to get correct order
        const newValues = column.satsueiMemoColumnValueList?.filter(value => 
          value.deleteFlg === 0 && value.isNew === true
        ) || []
        
        newValues.forEach((value, newValueIndex) => {
          values.push({
            satsueiMemoCategoryId: category.satsueiMemoCategoryId,
            satsueiMemoColumnId: column.satsueiMemoColumnId,
            satsueiMemoColumnValueId: value.satsueiMemoColumnValueId,
            satsueiMemoColumnValueName: value.satsueiMemoColumnValueName,
            initialValueFlg: value.initialValueFlg || 0,
            orderIndex: value.orderIndex || newValueIndex
          })
        })
      })
    })
    return values
  }, [categories])

  const prepareUpdatedColumnValueData = useCallback(() => {
    const values = []
    categories.forEach(category => {
      category.satsueiMemoColumnList?.forEach(column => {
        // Filter active, non-new values first to get correct order
        const activeValues = column.satsueiMemoColumnValueList?.filter(value => 
          value.deleteFlg === 0 && value.isNew !== true
        ) || []
        
        activeValues.forEach((value, activeValueIndex) => {
          values.push({
            satsueiMemoCategoryId: category.satsueiMemoCategoryId,
            satsueiMemoColumnId: column.satsueiMemoColumnId,
            satsueiMemoColumnValueId: value.satsueiMemoColumnValueId,
            satsueiMemoColumnValueName: value.satsueiMemoColumnValueName,
            initialValueFlg: value.initialValueFlg || 0,
            orderIndex: value.orderIndex || activeValueIndex,
            deleteFlg: value.deleteFlg
          })
        })
      })
    })
    return values
  }, [categories])

  const prepareRemovedColumnValueData = useCallback(() => {
    const values = []
    categories.forEach(category => {
      category.satsueiMemoColumnList?.forEach(column => {
        column.satsueiMemoColumnValueList?.forEach(value => {
          if (value.deleteFlg === 1) {
            values.push({
              satsueiMemoCategoryId: category.satsueiMemoCategoryId,
              satsueiMemoColumnId: column.satsueiMemoColumnId,
              satsueiMemoColumnValueId: value.satsueiMemoColumnValueId
            })
          }
        })
      })
    })
    return values
  }, [categories])

  return {
    // State
    categories,
    selectedCategoryId,
    selectedColumnId,
    selectedColumnValueId,
    valueCheckFlag,
    categoryNameList,
    allColumnList,
    tempCategoryNames,
    tempColumnNames,

    // Computed values
    activeCategories,
    deletedCategories,
    selectedCategory,
    activeColumns,
    deletedColumns,
    selectedColumn,
    activeColumnValues,
    deletedColumnValues,

    // Setters
    setCategories,
    setSelectedCategoryId,
    setSelectedColumnId,
    setSelectedColumnValueId,
    setValueCheckFlag,
    setCategoryNameList,
    setAllColumnList,

    // Operations
    addCategory,
    updateCategory,
    updateCategoryTemp,
    cancelCategoryEdit,
    deleteCategory,
    restoreCategory,
    addColumn,
    updateColumn,
    updateColumnTemp,
    cancelColumnEdit,
    deleteColumn,
    restoreColumn,
    addColumnValue,
    deleteColumnValue,
    restoreColumnValue,
    updateColumnInitialValue,
    updateColumnValue,

    // Validation
    validateRegistration,

    // Data preparation for new backend
    prepareNewCategoryData,
    prepareUpdatedCategoryData,
    prepareRemovedCategoryData,
    prepareNewColumnData,
    prepareUpdatedColumnData,
    prepareRemovedColumnData,
    prepareNewColumnValueData,
    prepareUpdatedColumnValueData,
    prepareRemovedColumnValueData,

    // Helpers
    getMaxCategoryId,
    getMaxColumnId,
    getMaxColumnValueId,
    isContainCategoryName,
    isContainColumnName,
    isContainColumnValueName
  }
}
